// CommunitySingle.js - Simplified version
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Backdrop,
  CircularProgress
} from '@mui/material';

// Import services and configs
import routes from 'config/routes';
import { getCommunity, getCommunityPublic } from 'api';

// Import components
import HomeCommunity from './components/homeCommunity';

// Import context
import { useProfile } from 'context';

const CommunitySingle = (props) => {
  const { profile } = props;
  const { addChatroom, chatrooms } = useProfile();
  const token = localStorage.getItem('token');
  const isAuthenticated = token && profile?.user_name || false;

  // Router hooks
  const params = useParams();
  const { slug } = params;
  const navigate = useNavigate();

  // State management
  const [community, setCommunity] = useState(null);
  const [members, setMembers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [communityBookings, setCommunityBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleExtended, setRoleExtended] = useState(false);
  const [my_membership, setMyMembership] = useState(null);
  const [recentMessages, setRecentMessages] = useState([]);

  // Fetch community information
  const getCommunityInfo = useCallback(() => {
    let fetchCommunity;

    if (profile && isAuthenticated) {
      fetchCommunity = getCommunity(slug);
    } else {
      fetchCommunity = getCommunityPublic(slug);
    }

    fetchCommunity
      .then(response => {
        if (response) {
          setCommunity(response.group);
          setMembers(response.members);
          setVenues(response.venues);
          setUpcomingEvents(response.upcoming_events);
          setCommunityBookings(response.community_bookings || []);
          setMyMembership(response.my_membership);
          setRecentMessages(response.messages);

          // Manage context chatrooms
          if (response.chat_rooms) {
            response.chat_rooms.forEach(chatRoom => {
              if (!chatrooms.some(existingChatRoom => existingChatRoom.id === chatRoom.id)) {
                addChatroom(chatRoom);
              }
            });
          }

          // Use response.my_membership directly (not the state variable which isn't updated yet)
          const membershipData = response.my_membership;
          if (membershipData) {
            // AD, FO, MA have full admin access, PT (Partner) and EO (Event Organizer) have limited access
            setRoleExtended(['AD', 'FO', 'MA', 'PT', 'EO'].includes(membershipData?.role));
          } else {
            setRoleExtended(false);
          }
          setLoading(false);
        }
      })
      .catch(error => {
        setLoading(false);
        console.error('Error fetching community:', error);
        navigate(routes.errorCommunity);
      });
  }, [slug, profile?.user_name, isAuthenticated, navigate, addChatroom, chatrooms]);

  const handleMembershipUpdate = useCallback((action, memberSlug, responseData) => {
    
    switch (action) {
      case 'role_changed':
        // Update the specific member's role in the members array
        setMembers(prevMembers => 
          prevMembers.map(member => 
            member.slug === memberSlug 
              ? { ...member, role: responseData.membership.role }
              : member
          )
        );
        break;
        
      case 'removed':
        // Remove the member from the array
        setMembers(prevMembers => 
          prevMembers.filter(member => member.slug !== memberSlug)
        );
        break;
        
      case 'approved':
      case 'rejected':
        // Update member status
        setMembers(prevMembers => 
          prevMembers.map(member => 
            member.slug === memberSlug 
              ? { ...member, status: responseData.membership_status }
              : member
          )
        );
        break;
        
      default:
        break;
    }
  }, []);


  // Initial data fetch
  useEffect(() => {
    getCommunityInfo();
  }, [getCommunityInfo]);

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {!loading ? (
        <HomeCommunity 
          slug={slug} 
          community={community}
          members={members}
          upcomingEvents={upcomingEvents}
          communityBookings={communityBookings}
          venues={venues}
          isAdmin={roleExtended}
          my_membership={my_membership}
          recentMessages={recentMessages}
          onEventsUpdate={() => getCommunityInfo(false)} // Add events refresh callback
          onMembershipUpdate={handleMembershipUpdate} // Add this!
          isAuthenticated={isAuthenticated}
          onShowLogin={() => navigate(routes.login)}
        />
      ) : (
        <Backdrop
          sx={{ color: '#fff', backgroundColor: 'rgba(0, 0, 0, 0.8)', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </Box>
  );
};

export default CommunitySingle;